// src/pages/public/APK.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { 
  Smartphone, 
  Clock,
  Bell,
  Mail,
  Users,
  Star,
  Shield
} from 'lucide-react'

const APK = () => {
  const features = [
    {
      icon: Smartphone,
      title: "Mobile First",
      description: "Optimized for seamless mobile experience",
      color: "blue"
    },
    {
      icon: Shield,
      title: "Secure & Safe",
      description: "Bank-level security for your investments",
      color: "green"
    },
    {
      icon: Users,
      title: "User Friendly",
      description: "Intuitive interface for all investors",
      color: "purple"
    },
    {
      icon: Star,
      title: "Premium Features",
      description: "Advanced tools for better investing",
      color: "yellow"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative inline-block mb-6">
              <Smartphone className="w-16 h-16 mx-auto" />
              <Clock className="w-8 h-8 absolute -top-2 -right-2 text-yellow-400" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Mobile{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                App
              </span>
            </h1>
            <div className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-full inline-flex items-center gap-2 mb-8">
              <Bell className="w-5 h-5" />
              <span className="font-semibold">Coming Soon</span>
            </div>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Our mobile app is under development. Get ready for the ultimate investment experience on the go.
            </p>
            <div className="flex items-center justify-center space-x-6 text-blue-200">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Join 50,000+ Users</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5" />
                <span>Premium Experience</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Amazing Features Coming Your Way
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              We're building a powerful mobile experience with everything you need to manage your investments
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 group-hover:scale-105 transition-all duration-300">
                  <div className={`bg-gradient-to-r from-${feature.color}-500 to-${feature.color}-600 w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Countdown & Notification */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl p-8 text-center text-white"
            >
              <Clock className="w-16 h-16 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">
                Stay Tuned for Launch
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                We're working hard to bring you the best mobile investment experience
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold mb-2">30+</div>
                  <div className="text-blue-100">Days to Launch</div>
                </div>
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold mb-2">95%</div>
                  <div className="text-blue-100">Development Complete</div>
                </div>
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold mb-2">50K+</div>
                  <div className="text-blue-100">Waiting Users</div>
                </div>
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-3xl font-bold mb-2">4.8★</div>
                  <div className="text-blue-100">Expected Rating</div>
                </div>
              </div>

              {/* Notification Form */}
              {/* <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-4">Get Notified When We Launch</h3>
                <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 rounded-xl border-0 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                  <button className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2 transition-all transform hover:scale-105">
                    <Mail className="w-5 h-5" />
                    Notify Me
                  </button>
                </div>
                <p className="text-blue-200 text-sm mt-3">
                  We'll send you one email when the app is ready. No spam.
                </p>
              </div> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Current Alternatives */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Use YesTimeEarn Today
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Access our platform while you wait for the mobile app
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 text-center"
            >
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Smartphone className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Mobile Web
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Access YesTimeEarn through your mobile browser with our responsive web design
              </p>
              <a
                href="https://udash.yestimeearn.com"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2 transition-all"
              >
                Go to Web App
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 text-center"
            >
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                User Dashboard
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Use our full-featured Web platform with advanced tools and analytics
              </p>
              <a
                href="https://udash.yestimeearn.com"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2 transition-all"
              >
                Open Dashboard
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default APK